// ---------------------------------------------------------------------------
// Shared visual constants for all canvas node views
// ---------------------------------------------------------------------------

import type { BitmapText, Graphics } from 'pixi.js'

// Colors
//
// The node *surface* colors — background, border, container body, and the
// placeholder shade — are theme-aware (ENG-518). They start at the classic/dark
// defaults and are reassigned by {@link setNodeThemeColors} when a themed canvas
// mounts: the Vesper Gray theme lifts them so canvas cards read as lifted
// surfaces against the gray canvas instead of near-black rectangles. They are
// `let` + live ESM bindings (not `const`) on purpose — every draw-time reader
// across the 11 node views picks up the active value automatically, so no node
// needs an engine reference threaded through its constructor. The setter is
// called by CanvasEngine from its config; the Vesper canvas remounts on theme
// change, so the values stay in lockstep with the active theme.
export let COLOR_BG = 0x1e1e1e
export const COLOR_SELECTION = 0xffffff
export const COLOR_SHADOW = 0x000000
export let COLOR_BORDER = 0x2a2a2a // subtle edge, barely visible
export const COLOR_HOVER_BORDER = 0x444444 // brightened on hover
/** Body fill for V3 container surfaces (BinGroupView, closed
 *  StackNodeView, expanded StackBackdropView). Sits between the canvas
 *  backdrop and COLOR_BG so containers read against the canvas while
 *  their contained asset cards stay the brightest figure inside them.
 *  Solid (alpha 1) so future content behind the canvas cannot shift
 *  the perceived shade. Theme-aware (see the note above). */
export let CONTAINER_BODY_FILL_COLOR = 0x1c1c1c
export let COLOR_PLACEHOLDER = 0x1e1e1e // tracks COLOR_BG so audio/set drafts read at the same shade as the image/shot thumbnail placeholder
export const COLOR_HOVER_OVERLAY = 0xffffff

/**
 * Theme-aware node surface colors. The defaults reproduce the classic/dark look;
 * the Vesper Gray theme supplies a lifted palette. `placeholder` always tracks
 * `background`, so it is derived rather than passed.
 */
export interface NodeThemeColors {
  background: number
  border: number
  containerBody: number
}

export const DEFAULT_NODE_THEME_COLORS: NodeThemeColors = {
  background: 0x1e1e1e,
  border: 0x2a2a2a,
  containerBody: 0x1c1c1c,
}

/**
 * Reassign the theme-aware node surface colors. Called by CanvasEngine from its
 * config on (re)construction — node views read the live bindings at draw time, so
 * this retints every node without threading a palette through each constructor.
 * Passing nothing restores the defaults (classic / dark), so every classic or
 * Vesper-black canvas resets any prior gray palette.
 */
export function setNodeThemeColors(colors: NodeThemeColors = DEFAULT_NODE_THEME_COLORS): void {
  COLOR_BG = colors.background
  COLOR_PLACEHOLDER = colors.background
  COLOR_BORDER = colors.border
  CONTAINER_BODY_FILL_COLOR = colors.containerBody
}
export const COLOR_TOP_BAR_DEFAULT = 0x000000
export const COLOR_TEXT = 0xcccccc
export const COLOR_TEXT_DIM = 0x808080
export const COLOR_TEXT_WHITE = 0xffffff
export const COLOR_ICON_MUTED = 0x666666

// Layout dimensions (match shared/canvasConstants.ts for parity)
export const NODE_WIDTH = 200
export const TOP_BAR_HEIGHT = 22
export const TOP_BAR_ALPHA = 0.95
export const ACCENT_LINE_HEIGHT = 3
export const BOTTOM_LABEL_OFFSET = 4
/**
 * Floor for a text annotation's content height. A note's real height is
 * content-driven and measured by `TextAnnotationDomLayer`; this is what an
 * unmeasured note starts at.
 */
export const TEXT_ANNOTATION_MIN_HEIGHT = 32

// Dimensions
export const CORNER_RADIUS = 6 // V3 design exploration: gentle softness on node corners (was 0 in parity)
export const BORDER_WIDTH = 1
export const SELECTION_BORDER_WIDTH = 1.5

// Bin annexation preview (C47-375)
// Live highlight painted around any node that will become a member of a
// bin if the user releases the resize handle now. Visually echoes the
// drop-target glow on BinGroupView so the gesture reads as akin to
// "drop here", just applied to the candidates rather than the container.
export const BIN_ANNEXATION_PREVIEW_STROKE_COLOR = COLOR_SELECTION
export const BIN_ANNEXATION_PREVIEW_STROKE_WIDTH = 2
export const BIN_ANNEXATION_PREVIEW_STROKE_ALPHA = 0.85
// Outset so the stroke sits clear of any selection/hover border the node
// already paints, instead of overlapping it pixel-for-pixel.
export const BIN_ANNEXATION_PREVIEW_OUTSET = 3

// Idle alpha for the stack frame; shared by closed `StackNodeView` and
// expanded `StackBackdropView` so toggling state doesn't pop a border in/out.
// Matches `BIN_BORDER_IDLE_ALPHA` in `BinGroupView.ts` so a stack frame and
// a bin border read at the same weight at idle.
export const STACK_FRAME_IDLE_ALPHA = 0.85

// Stroke width for the stack frame; shared by closed `StackNodeView` and
// expanded `StackBackdropView`. Matches `BIN_BORDER_STROKE_WIDTH` in
// `BinGroupView.ts` — slightly thicker than the asset-card `BORDER_WIDTH`
// so the container's shape stays consistent across the canvas.
export const STACK_FRAME_STROKE_WIDTH = 1.5

/**
 * Symmetric vertical padding inside the closed-stack frame. The thumbnail
 * sits centered between the top frame edge and the bottom edge; the
 * bottom-strip name label is rendered *below* the frame, offset by
 * `STACK_FRAME_BOTTOM_PAD` from the body bottom.
 */
export const STACK_FRAME_TOP_PAD = 0
export const STACK_FRAME_BOTTOM_PAD = 0
export const SHADOW_OFFSET = 2
export const SHADOW_ALPHA = 0.4
export const HOVER_OVERLAY_ALPHA = 0.06
export const DIMMED_ALPHA = 0.25

// Typography
//
// These string values are also registered as sharp bitmap-font atlases
// at engine init (see sharpText.ts).  BitmapFontManager keys its cache
// by family name, so as long as node views pass these exact strings as
// `fontFamily`, glyphs stay crisp at deep zoom.
export const FONT_FAMILY = 'Inter, system-ui, sans-serif' as const

/** Inline monospace — bin headers, stack headers. */
export const FONT_FAMILY_MONO_INLINE = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' as const

/** Courier-style monospace — Set node inline-viewer buttons. */
export const FONT_FAMILY_MONO_COURIER = 'monospace, Courier New, Courier' as const

// Color label map (matches frontend/src/lib/constants.ts COLOR_LABEL_MAP)
export const COLOR_LABEL_MAP: Record<string, number> = {
  green: 0x6cb887,
  yellow: 0xd4b65c,
  red: 0xd4736c,
  blue: 0x6b9fd4,
  purple: 0xa88bd4,
}

// Scrub progress bar (shared between ShotNodeView and StackNodeView)
/** Height of the scrub bar when actively scrubbing. */
export const SCRUB_BAR_ACTIVE_HEIGHT = 3
/** Height of the scrub bar in its passive state. */
export const SCRUB_BAR_PASSIVE_HEIGHT = 2
/** Track background alpha when actively scrubbing. */
export const SCRUB_TRACK_ACTIVE_ALPHA = 0.15
/** Track background alpha in passive state. */
export const SCRUB_TRACK_PASSIVE_ALPHA = 0.08
/** Fill alpha during active hover scrub (no playback). */
export const SCRUB_FILL_HOVER_ALPHA = 0.6
/** Fill alpha during active playback. */
export const SCRUB_FILL_PLAYBACK_ALPHA = 1.0
/** Font size for the scrub timecode label. */
export const SCRUB_TIMECODE_FONT_SIZE = 8
/** Alpha for the scrub timecode text. */
export const SCRUB_TIMECODE_ALPHA = 0.3
// Platform
export const IS_MAC = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)

// Content drag
/** Minimum pointer movement (px) before content drag activates. */
export const CONTENT_DRAG_THRESHOLD_PX = 3

/**
 * Trace a rectangle path with independent top / bottom corner radii. Each
 * corner can be 0 (square) or a positive radius. Used to build shapes that
 * align flush with neighbours of different curvature — e.g. the bottom-strip
 * color pill (square top, rounded bottom) sitting flush against the node
 * body (rounded top, square bottom). Caller fills/strokes the path after.
 */
export function tracePartialRoundedRect(
  g: Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  topRadius: number,
  bottomRadius: number,
): void {
  const t = topRadius
  const b = bottomRadius
  // Start at the top-left arc start (or the corner itself when t = 0).
  g.moveTo(x, y + t)
  if (t > 0) g.arc(x + t, y + t, t, Math.PI, Math.PI * 1.5)
  g.lineTo(x + width - t, y)
  if (t > 0) g.arc(x + width - t, y + t, t, -Math.PI / 2, 0)
  g.lineTo(x + width, y + height - b)
  if (b > 0) g.arc(x + width - b, y + height - b, b, 0, Math.PI / 2)
  g.lineTo(x + b, y + height)
  if (b > 0) g.arc(x + b, y + height - b, b, Math.PI / 2, Math.PI)
  g.lineTo(x, y + t)
  g.closePath()
}

/**
 * Draws a color-label accent strip whose top corners follow the node's
 * rounded-corner curvature and whose bottom is a flat line at `y = height`.
 * Assumes `height < radius` (e.g. the 3px strip on a 6px-radius node).
 */
export function drawTopAccentPath(g: Graphics, width: number, height: number, radius: number): void {
  const dx = Math.sqrt(radius * radius - (radius - height) * (radius - height))
  // Canvas/PixiJS angle convention: measured clockwise from +x axis.
  const aTop = -Math.PI / 2
  const aRight = Math.atan2(height - radius, dx)
  const aLeft = Math.atan2(height - radius, -dx)
  g.moveTo(radius, 0)
    .lineTo(width - radius, 0)
    .arc(width - radius, radius, radius, aTop, aRight)
    .lineTo(radius - dx, height)
    .arc(radius, radius, radius, aLeft, aTop)
    .closePath()
}

/**
 * LOD 0 wayfinding: tint a base color with a stable hue derived from the
 * node id so a wide-zoom canvas distinguishes nodes at a glance. The
 * tint is subtle (~18% lift on R/G/B independently) so nodes stay
 * readable on the dark canvas but the checker-board of identical gray
 * cells is broken up. Shared across all node views that LOD to a solid
 * rect at level 0 (ShotNodeView, ImageNodeView, JobNodeView, ...).
 */
export function tintStableHue(id: string, baseColor: number): number {
  // FNV-1a-ish hash — cheap, deterministic, no allocs.
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const r0 = (baseColor >> 16) & 0xff
  const g0 = (baseColor >> 8) & 0xff
  const b0 = baseColor & 0xff
  const dr = (h & 0x3f) % 48
  const dg = ((h >>> 8) & 0x3f) % 48
  const db = ((h >>> 16) & 0x3f) % 48
  const r = Math.min(255, r0 + dr)
  const g = Math.min(255, g0 + dg)
  const b = Math.min(255, b0 + db)
  return (r << 16) | (g << 8) | b
}

/**
 * Threshold (ms) for the bottom-strip label's manual click→double-click
 * detector across asset NodeViews. A second click within this window enters
 * rename mode; otherwise it's a single click (selection). Matches the engine's
 * `DBLCLICK_THRESHOLD_MS` for body-level dblclicks so user-perceived timing is
 * consistent.
 */
export const LABEL_DOUBLE_CLICK_THRESHOLD_MS = 400

/**
 * Set a BitmapText's content to `text`, truncating with an ellipsis when the
 * rendered width would exceed `maxWidth` at the current font. Mutates the label
 * in place.
 *
 * When `maxLines` > 1 the label's `wordWrap` is enabled so the text can flow
 * onto a second (or Nth) line. If the wrapped result exceeds `maxLines`, the
 * text is iteratively trimmed with an ellipsis until it fits.
 */
export function setBitmapTextWithEllipsis(label: BitmapText, text: string, maxWidth: number, maxLines = 1): void {
  // Prefix width/height are monotonic in prefix length (glyph advances are
  // positive; wrapping only adds lines as characters are added), so the
  // largest fitting prefix can be binary-searched. The prior linear
  // decrement was O(n) text set+measure calls per fit — noticeable once
  // zoom-driven refits truncate every visible label on a dense board.
  const maxHeightRef = { value: Number.POSITIVE_INFINITY }
  const fitsAt = (len: number): boolean => {
    label.text = len >= text.length ? text : text.slice(0, len) + '…'
    return label.height <= maxHeightRef.value && label.width <= maxWidth
  }

  if (maxLines > 1) {
    label.style.wordWrap = true
    // Pixi divides the run into lines against this width; a 0 (or negative)
    // budget is degenerate, so keep it positive and let the fit below do the
    // actual truncating.
    label.style.wordWrapWidth = Math.max(1, maxWidth)

    const probe = 'Ag' + '\nAg'.repeat(maxLines - 1)
    label.text = probe
    maxHeightRef.value = label.height
  } else {
    // A label can be re-fitted to one line after a multi-line fit (the
    // zoom-aware refit drops to one line as soon as the label counter-
    // scales). Leaving `wordWrap` on from the earlier call would keep
    // wrapping it, so the single-line path must turn it off explicitly.
    label.style.wordWrap = false
  }

  if (fitsAt(text.length) || text.length <= 1) return

  let lo = 0
  let hi = text.length - 1
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (fitsAt(mid)) lo = mid
    else hi = mid - 1
  }
  // Re-apply the winner — the final probe may have been a losing candidate.
  label.text = lo > 0 ? text.slice(0, lo) + '…' : '…'
}
